import { HOST_API_URL } from '../config';
import { IResponse } from '../types/types';
import axios from '../utils/axios';


export default class APIGateway<T> {

  public baseUrl: string = HOST_API_URL;

  /**
   * The axios interceptor rejects with `error.response.data`, which IS the API's own
   * response envelope. Return it as-is so `errorCode` and the real status (403, 429,
   * 500) survive instead of being flattened into a synthetic 400.
   *
   * Most responses are camelCase, but the auth middleware (400/403) and the global
   * exception handler (500) serialise with raw System.Text.Json and come back
   * PascalCase, so both spellings are accepted.
   */
  protected toErrorResponse(exp: any): IResponse {
    if (exp && typeof exp === 'object') {
      const statusCode = exp.statusCode ?? exp.StatusCode;

      if (typeof statusCode === 'number') {
        return {
          statusCode,
          message: exp.message ?? exp.Message ?? 'Request failed',
          errorCode: exp.errorCode ?? exp.ErrorCode ?? null,
          data: exp.data ?? exp.Data ?? null,
        };
      }
    }

    // The request never reached the API (network failure, CORS, DNS). There is no
    // server response to report, so no status and no error code are invented.
    return {
      statusCode: 0,
      message: 'Network error — request did not reach the API',
      errorCode: null,
      data: null,
    };
  }

  public async GetList(url: string, token?: string, isDefaultUrl?: boolean): Promise<IResponse | null> {
    let response: IResponse | null = null;
    try {
      if (!isDefaultUrl) {
        response = await axios.get(this.baseUrl + url, token ? {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        } : {});
      } else {
        response = await axios.get(url, token ? {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        } : {});
      }
      return response?.data;
    } catch (exp: any) {
      console.log(exp);
      return this.toErrorResponse(exp);
    }
  }

  public async GetSingle(url: string, token?: string, isDefaultUrl?: boolean): Promise<IResponse | null> {
    let response: IResponse | null = null;
    try {
      if (!isDefaultUrl) {
        response = await axios.get(this.baseUrl + url, token ? {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        } : {});
      } else {
        response = await axios.get(url, token ? {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        } : {});
      }
      return response?.data;
    } catch (exp: any) {
      console.log(exp);
      return this.toErrorResponse(exp);
    }
  }

  public async GetById(url: string, id: number, token?: string, isDefaultUrl?: boolean): Promise<IResponse | null> {
    let response: IResponse | null = null;
    try {
      if (!isDefaultUrl) {
        response = await axios.get(this.baseUrl + url + id, token ? {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        } : {});
      } else {
        response = await axios.get(url + id, token ? {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        } : {});
      }
      return response?.data;
    } catch (exp: any) {
      console.log(exp);
      return this.toErrorResponse(exp);
    }
  }

  public async Post(url: string, data: T, token?: string, isDefaultUrl?: boolean): Promise<IResponse | null> {
    let response: IResponse | null = null;
    try {
      if (!isDefaultUrl) {
        response = await axios.post(this.baseUrl + url, data, token ? {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        } : {});
      } else {
        response = await axios.post(url, data, token ? {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        } : {});
      }
      return response?.data;
    } catch (exp: any) {
      console.log(exp);
      return this.toErrorResponse(exp);
    }
  }

  public async PostFromAnotherModel<A>(url: string, data: A, token?: string, isDefaultUrl?: boolean): Promise<IResponse | null> {
    let response: IResponse | null = null;
    try {
      if (!isDefaultUrl) {
        response = await axios.post(this.baseUrl + url, data, token ? {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        } : {});
      } else {
        response = await axios.post(url, data, token ? {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        } : {});
      }
      return response?.data;
    } catch (exp: any) {
      console.log(exp);
      return this.toErrorResponse(exp);
    }
  }

  public async PostAppJson(url: string, data: T, token?: string, isDefaultUrl?: boolean): Promise<IResponse | null> {
    let response: IResponse | null = null;
    try {
      if (!isDefaultUrl) {
        response = await axios.post(this.baseUrl + url, data, token ? {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        } : {});
      } else {
        response = await axios.post(url, data, token ? {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        } : {});
      }
      return response?.data;
    } catch (exp: any) {
      console.log(exp);
      return this.toErrorResponse(exp);
    }
  }

  public async Put(url: string, data: T, token?: string, isDefaultUrl?: boolean): Promise<IResponse | null> {
    let response: IResponse | null = null;
    try {
      if (!isDefaultUrl) {
        response = await axios.put(this.baseUrl + url, data, token ? {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        } : {});
      } else {
        response = await axios.put(url, data, token ? {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        } : {});
      }
      return response?.data;
    } catch (exp: any) {
      console.log(exp);
      return this.toErrorResponse(exp);
    }
  }

  public async PutFromAnotherModel<A>(url: string, data: A, token?: string, isDefaultUrl?: boolean): Promise<IResponse | null> {
    let response: IResponse | null = null;
    try {
      if (!isDefaultUrl) {
        response = await axios.put(this.baseUrl + url, data, token ? {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        } : {});
      } else {
        response = await axios.put(url, data, token ? {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        } : {});
      }
      return response?.data;
    } catch (exp: any) {
      console.log(exp);
      return this.toErrorResponse(exp);
    }
  }

  public async Delete(url: string, token?: string, isDefaultUrl?: boolean): Promise<IResponse | null> {
    let response: IResponse | null = null;
    try {
      if (!isDefaultUrl) {
        response = await axios.delete(this.baseUrl + url, token ? {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        } : {});
      } else {
        response = await axios.delete(url, token ? {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        } : {});
      }
      return response?.data;
    } catch (exp: any) {
      console.log(exp);
      return this.toErrorResponse(exp);
    }
  }
}
